/**
 * Capital Market Assumptions (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_cma.py).
 *
 * Na URL: lookback (anos), período customizado (vazio = 1º de janeiro do ano do último
 * dado até o último dado) e aba. A aba Performance Acumulada vem de um endpoint próprio:
 * mudar o período customizado não recarrega as outras abas.
 */
import { Alert, Loader, NumberInput, Tabs } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { Elementos, type ElementoApi } from '../../components/Elementos';
import { BarraFiltros, CampoData, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { useFiltrosUrl } from '../../lib/url-state';
const CAMINHO = '/acompanhamentos-periodicos/capital-market-assumptions';
const PERFORMANCE = 'Performance Acumulada';
interface DadosCma {
  lookback: number;
  data_min: string;
  data_max: string;
  inicio_padrao: string;
  fim_padrao: string;
  cobertura: string | null;
  abas: { titulo: string; elementos: ElementoApi[] }[];
}
interface DadosPerformance {
  inicio: string;
  fim: string;
  elementos: ElementoApi[];
}
function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}
export function CapitalMarketAssumptions() {
  const [filtros, definir] = useFiltrosUrl({ lookback: 15, inicio: '', fim: '', aba: '' });
  const pagina = useQuery({
    queryKey: ['acompanhamentos.cma', filtros.lookback],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosCma>>(CAMINHO, { lookback: filtros.lookback }, signal),
    placeholderData: keepPreviousData,
  });
  const performance = useQuery({
    queryKey: ['acompanhamentos.cma.performance', filtros.lookback, filtros.inicio, filtros.fim],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosPerformance>>(
        `${CAMINHO}/performance`,
        {
          lookback: filtros.lookback,
          inicio: filtros.inicio || undefined,
          fim: filtros.fim || undefined,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  const dados = pagina.data?.data;
  const titulos = [PERFORMANCE, ...(dados?.abas.map((a) => a.titulo) ?? [])];
  const abaAtiva = titulos.includes(filtros.aba) ? filtros.aba : PERFORMANCE;
  // Datas efetivas (padrão ou limitadas aos dados pelo backend, como no legado).
  const inicio = performance.data?.data.inicio ?? filtros.inicio;
  const fim = performance.data?.data.fim ?? filtros.fim;
  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Capital Market Assumptions"
        descricao="Retorno, risco, assimetria, volatilidade e correlação das classes de ativos usadas na alocação."
        dadosAte={dados?.data_max}
        fonte="Banco Persevera (indicadores)"
      />
      <BarraFiltros carregando={pagina.isFetching || performance.isFetching}>
        <GrupoFiltros titulo="Histórico">
          <NumberInput
            label="Lookback (anos)"
            min={1}
            max={35}
            value={filtros.lookback}
            onChange={(v) =>
              typeof v === 'number' &&
              v >= 1 &&
              v <= 35 &&
              // Novo lookback muda o intervalo dos dados: o período volta ao padrão.
              definir({ lookback: v, inicio: '', fim: '' })
            }
            w={LARGURA.numero}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Período customizado">
          <CampoData
            rotulo="Início"
            valor={inicio}
            min={dados?.data_min}
            max={dados?.data_max}
            aoMudar={(data) => definir({ inicio: data })}
          />
          <CampoData
            rotulo="Fim"
            valor={fim}
            min={dados?.data_min}
            max={dados?.data_max}
            aoMudar={(data) => definir({ fim: data })}
          />
        </GrupoFiltros>
      </BarraFiltros>
      {pagina.isError && (
        <Alert color="red">{mensagem(pagina.error, 'Não foi possível carregar os dados.')}</Alert>
      )}
      {pagina.isPending && <Loader />}
      {dados && (
        <>
          <AvisosApi avisos={[pagina.data?.meta.avisos, performance.data?.meta.avisos]} />
          <Secao
            titulo="Classes de ativos"
            legenda={
              dados.cobertura ??
              'Métricas de longo prazo usam toda a janela disponível de cada ativo.'
            }
          >
            <Tabs
              value={abaAtiva}
              onChange={(aba) => aba && definir({ aba })}
              keepMounted={false}
              color="dourado"
            >
              <Tabs.List>
                {titulos.map((t) => (
                  <Tabs.Tab key={t} value={t}>
                    {t}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              <Tabs.Panel value={PERFORMANCE} pt="md">
                {performance.isError && (
                  <Alert color="red">
                    {mensagem(performance.error, 'Erro ao calcular a performance.')}
                  </Alert>
                )}
                {performance.data ? (
                  <Elementos elementos={performance.data.data.elementos} />
                ) : (
                  !performance.isError && <Loader />
                )}
              </Tabs.Panel>
              {dados.abas.map((aba) => (
                <Tabs.Panel key={aba.titulo} value={aba.titulo} pt="md">
                  <Elementos elementos={aba.elementos} />
                </Tabs.Panel>
              ))}
            </Tabs>
          </Secao>
        </>
      )}
    </Pagina>
  );
}
